import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Isolated async operations - never called from auth callbacks
  const loadProfile = useCallback(async (userId) => {
    if (!userId) return;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.single();

      if (!error) setUserProfile(data);
    } catch (error) {
      console.error('Profile load error:', error);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const clearProfile = useCallback(() => {
    setUserProfile(null);
    setProfileLoading(false);
  }, []);

  // Auth state handler - MUST remain synchronous
  const handleAuthChange = useCallback((event, session) => {
    console.log('[AuthContext] Auth state changed:', { 
      event, 
      hasSession: !!session, 
      userEmail: session?.user?.email 
    });
    
    setUser(session?.user ?? null);
    setLoading(false);

    if (session?.user) {
      loadProfile(session?.user?.id); // Fire-and-forget
    } else {
      clearProfile();
    }
  }, [loadProfile, clearProfile]);

  useEffect(() => {
    console.log('[AuthContext] Initializing auth...');
    
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      console.log('[AuthContext] Initial session loaded:', { 
        hasSession: !!session, 
        userEmail: session?.user?.email 
      });
      handleAuthChange(null, session);
    });

    // CRITICAL: This must remain synchronous
    const {
      data: { subscription }
    } = supabase?.auth?.onAuthStateChange(handleAuthChange);

    return () => {
      console.log('[AuthContext] Cleaning up auth subscription');
      subscription?.unsubscribe();
    };
  }, [handleAuthChange]);

  // Auth methods
  const signIn = async (emailOrCredentials, maybePassword) => {
    // Support both:
    // - signIn(email, password)
    // - signIn({ email, password })
    const { email, password } =
      typeof emailOrCredentials === 'string'
        ? { email: emailOrCredentials, password: maybePassword }
        : emailOrCredentials || {};

    if (!email || !password) {
      return { error: { message: 'Email and password are required.' } };
    }

    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      return { data, error };
    } catch (e) {
      console.error('Sign in error:', e);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        profileOperations?.clear();
      }
      return { error };
    } catch (e) {
      console.error('Sign out error:', e);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };

    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select()
        ?.single();

      if (!error) setUserProfile(data);
      return { data, error };
    } catch (e) {
      console.error('Update profile error:', e);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};